/**
 * Shared schedule-event form — used by the calendar "+ New event" flow and the
 * schedule detail edit flow.
 *
 * Spec 1 (service-call scheduling):
 *   - Native date + time pickers (no more strict "YYYY-MM-DD HH:MM" text).
 *   - End is OPTIONAL (open-ended events): a "No end" control clears it.
 *   - Notes (description) field.
 *   - Assignee picker fed by /api/account/users (real round-trip).
 *   - "Service call" type reveals a required Customer picker; on create the backend
 *     spins up + links a service-call Job (job_id) so Time & Materials can bill it.
 *
 * Emits a partial CreateScheduleEventRequest-shaped object; the caller creates or patches.
 * NEVER imports @smartbox/*. No raw style values.
 */
import { useState, type ComponentType } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import type { DateTimePickerEvent } from '@react-native-community/datetimepicker';
import { useQuery } from '@tanstack/react-query';
import { Card, Text, Field, Select, Button, Dropdown, CalendarField } from '../ui';
import { MicButton } from '../voice/MicButton';
import { theme } from '../../theme';
import { formatDateTime, isoToInputValue } from '../../lib/format';
import { SCHEDULE_EVENT_TYPE_OPTIONS } from '../../lib/scheduleMeta';
import { listUsers } from '../../api/account';
import { listCustomers } from '../../api/estimates';
import { createCustomer } from '../../api/crm';
import type { ScheduleEventDto, ScheduleEventType, Priority } from '@beam/api-types';

// Native module — present only in app binaries built after spec 1 shipped. Load it DEFENSIVELY so
// an OTA of this screen to an OLDER binary (which lacks the native module) never crashes: when it is
// absent we fall back to text date entry (the pre-spec-1 behavior). Everything else on this form is
// pure JS and ships via OTA regardless.
let DateTimePicker: ComponentType<{
  value: Date;
  mode: 'date' | 'time';
  display?: string;
  testID?: string;
  onChange: (e: DateTimePickerEvent, d?: Date) => void;
}> | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  DateTimePicker = require('@react-native-community/datetimepicker').default;
} catch {
  DateTimePicker = null;
}
const HAS_NATIVE_PICKER = DateTimePicker != null;

const PRIORITY_OPTIONS: ReadonlyArray<{ value: Priority; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CUSTOMER_MODE_OPTIONS: ReadonlyArray<{ value: 'existing' | 'new'; label: string }> = [
  { value: 'existing', label: 'Existing' },
  { value: 'new', label: 'New' },
];

const NEW_ENTRY_OPTIONS: ReadonlyArray<{ value: 'type' | 'speak'; label: string }> = [
  { value: 'type', label: 'Type' },
  { value: 'speak', label: 'Speak' },
];

const UNASSIGNED = '';

export type ScheduleEventFormValues = {
  event_type: ScheduleEventType;
  title: string;
  description: string | null;
  starts_at: string | null;
  ends_at: string | null;
  all_day: boolean;
  is_flexible: boolean;
  priority: Priority;
  assigned_to: string | null;
  /** Only meaningful when event_type === 'service_call' (creates + links a Job). */
  customer_id: string | null;
};

function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "HH:MM" (24h) for the time portion of an ISO instant, or '' when absent. */
function isoToTimeInput(iso: string | null | undefined): string {
  const v = isoToInputValue(iso); // "YYYY-MM-DD HH:MM" or ''
  return v ? (v.split(' ')[1] ?? '') : '';
}

/**
 * Combine a picked calendar date with an optional "HH:MM" time into an ISO instant. When the time
 * is blank or malformed, the date's own time-of-day is kept (CalendarField picks local noon), so a
 * date-only choice still yields a valid instant. Returns null when no date is chosen.
 */
function combineDateTime(date: Date | null, time: string): string | null {
  if (!date) return null;
  const d = new Date(date.getTime());
  const m = time.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (m) d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d.toISOString();
}

/** Replace the Y/M/D of `base` with those of `picked`, keeping the time. */
function withDate(base: Date, picked: Date): Date {
  const d = new Date(base);
  d.setFullYear(picked.getFullYear(), picked.getMonth(), picked.getDate());
  return d;
}
/** Replace the H/M of `base` with those of `picked`, keeping the date. */
function withTime(base: Date, picked: Date): Date {
  const d = new Date(base);
  d.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
  return d;
}

type PickerTarget = { field: 'start' | 'end'; mode: 'date' | 'time' } | null;

export function ScheduleEventForm({
  initial,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: ScheduleEventDto;
  busy: boolean;
  submitLabel: string;
  onSubmit: (values: ScheduleEventFormValues) => void;
  onCancel: () => void;
}) {
  const [eventType, setEventType] = useState<ScheduleEventType>(
    (initial?.event_type as ScheduleEventType) ?? 'site_visit',
  );
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [startsAt, setStartsAt] = useState<Date | null>(isoToDate(initial?.starts_at));
  const [endsAt, setEndsAt] = useState<Date | null>(isoToDate(initial?.ends_at));
  // Text fallback state (used only when the native picker is unavailable — older binaries).
  const [startTime, setStartTime] = useState(isoToTimeInput(initial?.starts_at));
  const [endTime, setEndTime] = useState(isoToTimeInput(initial?.ends_at));
  const [allDay, setAllDay] = useState(initial?.all_day ?? false);
  const [isFlexible, setIsFlexible] = useState(initial?.is_flexible ?? false);
  const [priority, setPriority] = useState<Priority>((initial?.priority as Priority) ?? 'medium');
  const [assignedTo, setAssignedTo] = useState<string>(initial?.assigned_to ?? UNASSIGNED);
  const [customerId, setCustomerId] = useState<string>('');
  // Customer attach (all event types): pick an existing one or quick-create a new one by
  // speaking or typing just the name (mobile UX batch B).
  const [customerMode, setCustomerMode] = useState<'existing' | 'new'>('existing');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newEntryMode, setNewEntryMode] = useState<'type' | 'speak'>('type');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [picker, setPicker] = useState<PickerTarget>(null);
  const [err, setErr] = useState<string | null>(null);

  const isServiceCall = eventType === 'service_call';

  const usersQuery = useQuery({ queryKey: ['account-users'], queryFn: listUsers, staleTime: 60_000 });
  // Customers can now attach to ANY event type, so fetch them unconditionally.
  const customersQuery = useQuery({ queryKey: ['customers'], queryFn: listCustomers, staleTime: 60_000 });

  const assigneeOptions = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...(usersQuery.data ?? []).map((u) => ({ value: u.id, label: u.full_name })),
  ];
  const customerOptions = (customersQuery.data ?? []).map((c) => ({ value: c.id, label: c.displayName }));

  function onPickerChange(event: DateTimePickerEvent, selected?: Date) {
    const cur = picker;
    if (!cur || event.type === 'dismissed' || !selected) {
      setPicker(null);
      return;
    }
    const current = cur.field === 'start' ? startsAt : endsAt;
    const base = current ?? selected;
    if (cur.mode === 'date') {
      const merged = withDate(base, selected);
      if (cur.field === 'start') setStartsAt(merged);
      else setEndsAt(merged);
      setPicker({ field: cur.field, mode: 'time' }); // chain into the time step
    } else {
      const merged = withTime(base, selected);
      if (cur.field === 'start') setStartsAt(merged);
      else setEndsAt(merged);
      setPicker(null);
    }
  }

  const submit = async () => {
    setErr(null);
    if (!title.trim()) {
      setErr('Title is required.');
      return;
    }
    // A service call needs a customer (existing selection or a new name to create).
    if (isServiceCall) {
      const hasCustomer = customerMode === 'existing' ? !!customerId : !!newCustomerName.trim();
      if (!hasCustomer) {
        setErr('Choose or add a customer for the service call.');
        return;
      }
    }
    // Resolve start/end from whichever entry mode is active.
    let startsIso: string | null;
    let endsIso: string | null;
    if (HAS_NATIVE_PICKER) {
      startsIso = startsAt ? startsAt.toISOString() : null;
      endsIso = endsAt ? endsAt.toISOString() : null;
    } else {
      // Fallback (binary without the native module): a tap-a-calendar date + an optional HH:MM time.
      startsIso = combineDateTime(startsAt, startTime);
      endsIso = combineDateTime(endsAt, endTime);
    }
    if (startsIso && endsIso && new Date(endsIso).getTime() < new Date(startsIso).getTime()) {
      setErr('End must be after the start.');
      return;
    }
    // Resolve the customer: an existing selection, or quick-create a new one from the name.
    let resolvedCustomerId: string | null = null;
    if (customerMode === 'existing') {
      resolvedCustomerId = customerId || null;
    } else {
      const name = newCustomerName.trim();
      if (!name) {
        // Only block when a new customer is actually required (service call) or partially entered.
        if (isServiceCall) {
          setErr('Enter a customer name, or switch to an existing customer.');
          return;
        }
      } else {
        try {
          setCreatingCustomer(true);
          const created = await createCustomer({ customer_type: 'residential', display_name: name });
          resolvedCustomerId = created.id;
        } catch (e) {
          setErr(e instanceof Error ? e.message : 'Could not create the customer.');
          return;
        } finally {
          setCreatingCustomer(false);
        }
      }
    }
    onSubmit({
      event_type: eventType,
      title: title.trim(),
      description: description.trim() ? description.trim() : null,
      starts_at: startsIso,
      ends_at: endsIso,
      all_day: allDay,
      is_flexible: isFlexible,
      priority,
      assigned_to: assignedTo || null,
      customer_id: resolvedCustomerId,
    });
  };

  return (
    <Card>
      <Text variant="bodyStrong">{initial ? 'Edit event' : 'New event'}</Text>
      <Dropdown label="Job Type" options={SCHEDULE_EVENT_TYPE_OPTIONS} value={eventType} onChange={setEventType} />

      {/* Customer — attachable to ANY event type; a service call also spins up a linked Job. */}
      <View style={styles.customerSection}>
        <Select label="Customer" options={CUSTOMER_MODE_OPTIONS} value={customerMode} onChange={setCustomerMode} />
        {customerMode === 'existing' ? (
          <Dropdown
            label="Choose customer"
            options={customerOptions}
            value={customerId}
            onChange={setCustomerId}
            placeholder="Select customer…"
          />
        ) : (
          <>
            <Select label="Enter by" options={NEW_ENTRY_OPTIONS} value={newEntryMode} onChange={setNewEntryMode} />
            <View style={styles.newCustomerRow}>
              <View style={styles.newCustomerField}>
                <Field
                  label="New customer name"
                  placeholder="e.g. Jane Homeowner"
                  value={newCustomerName}
                  onChangeText={setNewCustomerName}
                  accessibilityLabel="New customer name"
                />
              </View>
              {newEntryMode === 'speak' ? (
                <MicButton
                  onTranscribe={(t) => setNewCustomerName((n) => (n.trim() ? `${n} ${t}` : t))}
                  label=""
                  size="md"
                  accessibilityLabel="Dictate customer name"
                />
              ) : null}
            </View>
          </>
        )}
      </View>

      <Field
        label="Title"
        placeholder="What is happening?"
        value={title}
        onChangeText={setTitle}
        accessibilityLabel="Event title"
      />

      <Field
        label="Notes"
        placeholder="Details, gate codes, parts to bring…"
        value={description}
        onChangeText={setDescription}
        multiline
        numberOfLines={3}
        accessibilityLabel="Event notes"
      />

      {HAS_NATIVE_PICKER ? (
        <>
          <DateTimeRow
            label="Starts"
            value={startsAt}
            onPick={() => setPicker({ field: 'start', mode: 'date' })}
            onClear={() => setStartsAt(null)}
          />
          <DateTimeRow
            label="Ends (optional)"
            value={endsAt}
            emptyLabel="No end"
            onPick={() => setPicker({ field: 'end', mode: 'date' })}
            onClear={() => setEndsAt(null)}
          />
          {picker && DateTimePicker ? (
            <DateTimePicker
              testID={`picker-${picker.field}-${picker.mode}`}
              value={(picker.field === 'start' ? startsAt : endsAt) ?? new Date()}
              mode={picker.mode}
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={onPickerChange}
            />
          ) : null}
        </>
      ) : (
        <>
          <CalendarField
            label="Starts"
            value={startsAt}
            onChange={setStartsAt}
            placeholder="Pick a start date"
            accessibilityLabel="Event start date"
          />
          {!allDay ? (
            <Field
              label="Start time (optional, HH:MM)"
              placeholder="09:00"
              value={startTime}
              onChangeText={setStartTime}
              autoCapitalize="none"
              accessibilityLabel="Event start time"
            />
          ) : null}
          <CalendarField
            label="Ends (optional)"
            value={endsAt}
            onChange={setEndsAt}
            placeholder="No end date"
            accessibilityLabel="Event end date"
          />
          {!allDay && endsAt ? (
            <Field
              label="End time (optional, HH:MM)"
              placeholder="11:00"
              value={endTime}
              onChangeText={setEndTime}
              autoCapitalize="none"
              accessibilityLabel="Event end time"
            />
          ) : null}
        </>
      )}

      <Select
        label="Assignee"
        options={assigneeOptions}
        value={assignedTo}
        onChange={setAssignedTo}
      />
      <Select
        label="All day?"
        options={[
          { value: 'no', label: 'Timed' },
          { value: 'yes', label: 'All day' },
        ]}
        value={allDay ? 'yes' : 'no'}
        onChange={(v) => setAllDay(v === 'yes')}
      />
      <Select
        label="Scheduling"
        options={[
          { value: 'fixed', label: 'Fixed' },
          { value: 'flexible', label: 'Flexible' },
        ]}
        value={isFlexible ? 'flexible' : 'fixed'}
        onChange={(v) => setIsFlexible(v === 'flexible')}
      />
      <Select label="Priority" options={PRIORITY_OPTIONS} value={priority} onChange={setPriority} />
      {err ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {err}
        </Text>
      ) : null}
      <View style={styles.actions}>
        <Button
          title={submitLabel}
          onPress={() => {
            void submit();
          }}
          loading={busy || creatingCustomer}
          disabled={busy || creatingCustomer}
        />
        <Button title="Cancel" variant="secondary" onPress={onCancel} disabled={busy || creatingCustomer} />
      </View>
    </Card>
  );
}

function DateTimeRow({
  label,
  value,
  emptyLabel = 'Not set',
  onPick,
  onClear,
}: {
  label: string;
  value: Date | null;
  emptyLabel?: string;
  onPick: () => void;
  onClear: () => void;
}) {
  return (
    <View style={styles.dtRow}>
      <Text variant="label" color="secondary">
        {label}
      </Text>
      <View style={styles.dtControls}>
        <Pressable
          onPress={onPick}
          style={styles.dtValue}
          accessibilityRole="button"
          accessibilityLabel={`Set ${label} date and time`}
        >
          <Text variant="body">{value ? formatDateTime(value.toISOString()) : emptyLabel}</Text>
        </Pressable>
        {value ? (
          <Button
            title="Clear"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={onClear}
            accessibilityLabel={`Clear ${label}`}
          />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: { gap: theme.spacing.sm },
  customerSection: { gap: theme.spacing.sm },
  newCustomerRow: { flexDirection: 'row', alignItems: 'flex-end', gap: theme.spacing.sm },
  newCustomerField: { flex: 1 },
  dtRow: { gap: theme.spacing.xs },
  dtControls: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  dtValue: {
    flex: 1,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.radius.md,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    backgroundColor: theme.color.bg.surface,
  },
});
